import { useState, useCallback, useRef } from 'react';
import { buildingDocumentsApi, FileDocument, UploadProgress } from '../../api/buildingDocumentsApi';

export interface PendingFileUpload {
    file: File;
    name: string;
    category: string;
}

export type UploadStatus = 'pending' | 'uploading' | 'completed' | 'error';

export interface FileUploadProgress {
    status: UploadStatus;
    progress: number; // 0-100
    error?: string;
}

interface UseDocumentUploadOptions {
    maxConcurrent?: number;
    throttleMs?: number;
    onComplete?: () => void;
    onError?: (error: string) => void;
}

interface UseDocumentUploadReturn {
    uploadProgress: Record<number, FileUploadProgress>;
    isUploading: boolean;
    uploadFiles: (buildingKey: string, uploads: PendingFileUpload[]) => Promise<FileDocument[]>;
    cancelUpload: () => void;
}

/**
 * Hook for handling multi-file document uploads with concurrent pool and throttled progress
 */
export const useDocumentUpload = (options: UseDocumentUploadOptions = {}): UseDocumentUploadReturn => {
    const {
        maxConcurrent = 3,
        throttleMs = 150,
        onComplete,
        onError
    } = options;

    const [uploadProgress, setUploadProgress] = useState<Record<number, FileUploadProgress>>({});
    const [isUploading, setIsUploading] = useState(false);

    // Track last progress update time for throttling
    const lastUpdateTime = useRef<Record<number, number>>({});

    // Track if upload should be cancelled
    const cancelledRef = useRef(false);

    const updateProgress = useCallback((index: number, progress: FileUploadProgress) => {
        const now = Date.now();
        const lastUpdate = lastUpdateTime.current[index] || 0;

        // Always update for status changes or completion, throttle progress updates
        if (progress.status !== 'uploading' || now - lastUpdate >= throttleMs) {
            lastUpdateTime.current[index] = now;
            setUploadProgress(prev => ({
                ...prev,
                [index]: progress
            }));
        }
    }, [throttleMs]);

    const uploadSingleFile = useCallback(async (
        buildingKey: string,
        upload: PendingFileUpload,
        index: number
    ): Promise<FileDocument | null> => {
        if (cancelledRef.current) return null;

        updateProgress(index, { status: 'uploading', progress: 0 });

        try {
            const document = await buildingDocumentsApi.uploadDocument(
                buildingKey,
                upload.file,
                upload.name,
                upload.category,
                (progress: UploadProgress) => {
                    if (!cancelledRef.current) {
                        updateProgress(index, { status: 'uploading', progress: progress.progress });
                    }
                }
            );

            updateProgress(index, { status: 'completed', progress: 100 });
            return document;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Uppladdning misslyckades';
            updateProgress(index, { status: 'error', progress: 0, error: errorMessage });
            return null;
        }
    }, [updateProgress]);

    const uploadFiles = useCallback(async (
        buildingKey: string,
        uploads: PendingFileUpload[]
    ): Promise<FileDocument[]> => {
        if (uploads.length === 0) return [];

        cancelledRef.current = false;
        setIsUploading(true);
        lastUpdateTime.current = {};

        // Initialize progress for all files
        const initialProgress: Record<number, FileUploadProgress> = {};
        uploads.forEach((_, index) => {
            initialProgress[index] = { status: 'pending', progress: 0 };
        });
        setUploadProgress(initialProgress);

        const uploadedDocuments: FileDocument[] = [];

        // Process uploads with concurrent pool
        let nextIndex = 0;
        const activeUploads = new Map<number, Promise<FileDocument | null>>();

        const startUpload = (index: number) => {
            const promise = uploadSingleFile(buildingKey, uploads[index], index).finally(() => {
                activeUploads.delete(index);
            });
            activeUploads.set(index, promise);
            return promise;
        };

        // Start initial concurrent uploads
        while (nextIndex < uploads.length && activeUploads.size < maxConcurrent) {
            startUpload(nextIndex);
            nextIndex++;
        }

        // Continue until all uploads are complete
        while (activeUploads.size > 0) {
            const completed = await Promise.race(activeUploads.values());
            if (completed) {
                uploadedDocuments.push(completed);
            }

            // Start new uploads to fill available slots
            while (nextIndex < uploads.length && activeUploads.size < maxConcurrent && !cancelledRef.current) {
                startUpload(nextIndex);
                nextIndex++;
            }
        }

        setIsUploading(false);

        // Check if any uploads failed
        const hasErrors = Object.values(uploadProgress).some(p => p.status === 'error');
        if (hasErrors && onError) {
            onError('Några filer kunde inte laddas upp');
        }

        if (onComplete) {
            onComplete();
        }

        return uploadedDocuments;
    }, [uploadSingleFile, maxConcurrent, onComplete, onError, uploadProgress]);

    const cancelUpload = useCallback(() => {
        cancelledRef.current = true;
        setIsUploading(false);
    }, []);

    return {
        uploadProgress,
        isUploading,
        uploadFiles,
        cancelUpload
    };
};
